// transactionProcessIndytendCustom.js
// ALIGNMENT NOTE:
// This front-end process definition MUST mirror the backend process.edn used by Sharetribe.
// A recent backend update introduced new transitions (confirm-payment, external-payment-complete)
// and different state ordering (inquiry -> pending-payment -> accepted ...).
// Because we whitelist lastTransitions in Inbox queries, any transition missing here
// causes those transactions to disappear from the Inbox after that transition fires.
// Root cause of disappearing bookings after payment: missing confirm-payment/external-payment-complete
// in this transitions map + mismatched state names (payment-pending vs pending-payment).
// This file now mirrors process/process.edn.

import { getProcess } from './transaction';

// Exported transition name constants (must include ALL used by backend process.edn)
export const transitions = {
  INQUIRE: 'transition/inquire',
  ACCEPT_INQUIRY: 'transition/accept-inquiry',
  DECLINE_INQUIRY: 'transition/decline-inquiry',
  CONFIRM_PAYMENT: 'transition/confirm-payment', // customer (internal Stripe confirm)
  EXTERNAL_PAYMENT_COMPLETE: 'transition/external-payment-complete', // SCaT external payment success
  RECONFIRM: 'transition/reconfirm',
  COMPLETE: 'transition/complete',
  PAYOUT: 'transition/payout',
  REVIEW: 'transition/review',
};

// Canonical state names (match :state/... keywords without the prefix)
export const states = {
  INITIAL: 'initial', // synthetic (not usually used directly)
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  ACCEPTED: 'accepted',
  RECONFIRMED: 'reconfirmed',
  COMPLETED: 'completed',
  PAYOUT: 'payout',
  REVIEWED: 'reviewed',
  DECLINED: 'declined',
};

// Graph mirrors backend process.edn
export const graph = {
  states: {
    initial: {
      on: { 'transition/inquire': 'inquiry' },
    },
    inquiry: {
      on: {
        'transition/accept-inquiry': 'pending-payment',
        'transition/decline-inquiry': 'declined',
      },
    },
    'pending-payment': {
      on: {
        'transition/confirm-payment': 'accepted',
        'transition/external-payment-complete': 'accepted',
      },
    },
    accepted: {
      on: { 'transition/reconfirm': 'reconfirmed' },
    },
    reconfirmed: {
      on: { 'transition/complete': 'completed' },
    },
    completed: {
      on: { 'transition/payout': 'payout' },
    },
    payout: {
      on: { 'transition/review': 'reviewed' },
    },
    reviewed: {},
    declined: {},
  },
};

// Attention states (UI cues). Adjust if UX changes.
export const statesNeedingCustomerAttention = {
  PENDING_PAYMENT: states.PENDING_PAYMENT,
  REVIEWED: states.REVIEWED, // customer may still leave review earlier in some flows – keep for symmetry
};

export const statesNeedingProviderAttention = {
  INQUIRY: states.INQUIRY,
  ACCEPTED: states.ACCEPTED, // provider can reconfirm
  RECONFIRMED: states.RECONFIRMED, // can complete
};

export const isRelevantPastTransition = transition => Object.values(transitions).includes(transition);

export const isPrivileged = transition => [transitions.PAYOUT].includes(transition);

export const isCompleted = transition => [transitions.COMPLETE, transitions.PAYOUT, transitions.REVIEW].includes(transition);

export const isRefunded = () => false;

export const isCustomerReview = transition => transition === transitions.REVIEW;
export const isProviderReview = transition => false; // single review transition

// Helper to get current state via generic resolver
export const getCurrentState = tx => {
  const process = getProcess('indytend-custom-booking');
  return process.getState(tx);
};

export const getStateLabel = state => {
  switch (state) {
    case states.INQUIRY:
      return 'Inquiry';
    case states.PENDING_PAYMENT:
      return 'Awaiting Payment';
    case states.ACCEPTED:
      return 'Accepted';
    case states.RECONFIRMED:
      return 'Reconfirmed';
    case states.COMPLETED:
      return 'Completed';
    case states.PAYOUT:
      return 'Payout';
    case states.REVIEWED:
      return 'Reviewed';
    case states.DECLINED:
      return 'Declined';
    default:
      return 'Unknown';
  }
};

// Usage remains the same; ensure backend & frontend stay in sync when editing process.edn
